/*
 * dm-cart.js
 * Cart drawer, cart page lines, accessory upsell and free-shipping progress.
 * The Shopify cart is authoritative: every change goes through the Ajax Cart API
 * (/cart.js, /cart/add.js, /cart/change.js) and the UI is re-rendered from the response.
 */
(function () {
  'use strict';

  var doc = document;
  var DM = window.DM;
  var S = window.DMShipping;
  if (!DM || !S) return;

  var cfg = DM.config;
  var str = DM.strings;
  var routes = cfg.routes;
  var shipOpts = { threshold: cfg.shipping.threshold, standard: cfg.shipping.standard };

  var drawer = doc.getElementById('dm-cart-drawer');
  var state = {
    cart: null,
    adding: false,
    lineBusy: {},
    upsellSel: {},
    upsellBusy: {},
    lastShipState: null
  };

  var upsellData = [];
  try {
    var raw = doc.getElementById('dm-upsell-data');
    upsellData = raw ? JSON.parse(raw.textContent) || [] : [];
  } catch (e) {
    upsellData = [];
  }
  var triggerHandles = String(cfg.upsell.triggerHandles || '')
    .split(',')
    .map(function (h) {
      return h.trim();
    })
    .filter(Boolean);

  /* ---------- network ---------- */
  function request(url, body) {
    var options = {
      method: body ? 'POST' : 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    };
    if (body) {
      options.headers['Content-Type'] = 'application/json';
      options.body = JSON.stringify(body);
    }
    return fetch(url, options).then(function (res) {
      return res.text().then(function (text) {
        var data = null;
        try {
          data = text ? JSON.parse(text) : null;
        } catch (e) {
          data = null;
        }
        if (!res.ok || !data) {
          var err = new Error((data && (data.description || data.message)) || 'HTTP ' + res.status);
          err.status = res.status;
          err.data = data;
          throw err;
        }
        return data;
      });
    });
  }

  function fetchCart() {
    return request(routes.cartJson).then(setCart);
  }

  function setCart(cart) {
    state.cart = cart;
    render();
    doc.dispatchEvent(new CustomEvent('dm:cart:updated', { detail: { cart: cart } }));
    return cart;
  }

  function friendly(err) {
    return err && err.data && err.data.description ? err.data.description : str.addError;
  }

  /* ---------- add and change ---------- */
  function add(items, options) {
    options = options || {};
    if (state.adding) return Promise.reject(new Error('busy'));
    state.adding = true;
    hideError();
    // Remember what had focus: the add button is disabled while the request runs.
    var opener = options.opener || doc.activeElement;
    return request(routes.cartAdd, { items: items })
      .then(function () {
        return request(routes.cartJson);
      })
      .then(function (cart) {
        state.adding = false;
        setCart(cart);
        var names = [];
        items.forEach(function (it) {
          var line = findLineByVariant(cart, it.id);
          if (line) names.push(line.product_title);
        });
        if (names.length) DM.announce(DM.fmt(str.added, { title: names[0] }));
        if (options.open !== false) open(opener);
        return cart;
      })
      .catch(function (err) {
        state.adding = false;
        throw err;
      });
  }

  function change(key, quantity, title) {
    if (state.lineBusy[key]) return Promise.resolve();
    state.lineBusy[key] = true;
    markBusy(key, true);
    hideError();
    return request(routes.cartChange, { id: key, quantity: quantity })
      .then(function (cart) {
        delete state.lineBusy[key];
        setCart(cart);
        if (quantity === 0) DM.announce(DM.fmt(str.removed, { title: title || '' }));
        else DM.announce(str.cartUpdated);
      })
      .catch(function (err) {
        delete state.lineBusy[key];
        // Restore the last confirmed state, then re-sync with Shopify.
        render();
        showError(friendly(err) === str.addError ? str.updateError : friendly(err));
        fetchCart().catch(function () {});
      });
  }

  function findLineByVariant(cart, variantId) {
    for (var i = 0; i < cart.items.length; i++) {
      if (String(cart.items[i].variant_id) === String(variantId)) return cart.items[i];
    }
    return null;
  }

  /* ---------- open and close ---------- */
  function open(opener) {
    if (!drawer || !cfg.cartDrawer) {
      window.location.href = routes.cart;
      return;
    }
    DM.modal.open(drawer, opener);
  }
  function close() {
    if (drawer) DM.modal.close(drawer);
  }

  if (drawer) {
    drawer.addEventListener('dm:modal:open', function () {
      fetchCart().catch(function () {});
    });
  }

  doc.addEventListener('click', function (event) {
    var link = event.target instanceof Element ? event.target.closest('[data-dm-cart-open]') : null;
    if (link && drawer && cfg.cartDrawer) {
      event.preventDefault();
      open(link);
    }
  });

  /* ---------- errors ---------- */
  function errorBoxes() {
    return DM.$$('[data-dm-cart-error]');
  }
  function showError(message) {
    errorBoxes().forEach(function (box) {
      box.textContent = message;
      box.hidden = false;
    });
  }
  function hideError() {
    errorBoxes().forEach(function (box) {
      box.hidden = true;
      box.textContent = '';
    });
  }

  /* ---------- rendering ---------- */
  function render() {
    var cart = state.cart;
    if (!cart) return;
    updateCount(cart);
    updateShipping(cart);
    DM.$$('[data-dm-cart-lines]').forEach(function (host) {
      renderInto(host, function () {
        host.innerHTML = linesHtml(cart);
      });
    });
    DM.$$('[data-dm-upsell]').forEach(function (host) {
      renderInto(host, function () {
        host.innerHTML = upsellHtml(cart);
      });
    });
    DM.$$('[data-dm-cart-foot]').forEach(function (host) {
      renderInto(host, function () {
        footHtml(host, cart);
      });
    });
  }

  /* Re-render a region and put keyboard focus back on the same control. */
  function renderInto(host, fn) {
    var active = doc.activeElement;
    var focusId = active && host.contains(active) ? active.getAttribute('data-dm-focus') : null;
    fn();
    if (focusId) {
      var again = host.querySelector('[data-dm-focus="' + focusId + '"]');
      if (again && !again.disabled) again.focus({ preventScroll: true });
    }
  }

  function itemWord(count) {
    return count === 1 ? str.itemSingular : str.itemPlural;
  }

  function updateCount(cart) {
    DM.$$('[data-dm-cart-count]').forEach(function (el) {
      el.textContent = cart.item_count;
      el.hidden = cart.item_count === 0;
    });
    DM.$$('[data-dm-cart-count-text]').forEach(function (el) {
      el.textContent = cart.item_count + ' ' + itemWord(cart.item_count);
    });
    DM.$$('.dm-cart-link').forEach(function (el) {
      el.setAttribute('aria-label', 'Cart, ' + cart.item_count + ' ' + itemWord(cart.item_count));
    });
  }

  function shipMessage(sum) {
    if (sum.state === 'unlocked') return str.shipUnlocked;
    if (sum.state === 'below') return DM.fmt(str.shipBelow, { amount: DM.money(sum.remaining) });
    return DM.fmt(str.shipEmpty, { amount: DM.money(sum.threshold) });
  }

  function updateShipping(cart) {
    var sum = S.summarize(cart, shipOpts);
    var message = shipMessage(sum);
    DM.$$('[data-dm-ship]').forEach(function (el) {
      el.setAttribute('data-state', sum.state);
      el.classList.remove('dm-ship--empty', 'dm-ship--below', 'dm-ship--unlocked');
      el.classList.add('dm-ship--' + sum.state);
      var text = el.querySelector('[data-dm-ship-text]');
      if (text) text.textContent = message;
      var bar = el.querySelector('[data-dm-ship-bar]');
      if (bar) {
        bar.setAttribute('aria-valuenow', String(Math.round(sum.percent)));
        bar.setAttribute('aria-valuetext', message);
      }
      var fill = el.querySelector('[data-dm-ship-fill]');
      if (fill) fill.style.width = sum.percent + '%';
    });
    DM.$$('[data-dm-ship-hint]').forEach(function (el) {
      var show = sum.state === 'below';
      el.hidden = !show;
      if (show) el.textContent = message;
    });
    if (state.lastShipState && state.lastShipState !== 'unlocked' && sum.state === 'unlocked') {
      DM.announce(str.shipUnlocked);
    }
    state.lastShipState = sum.state;
  }

  function optionsText(item) {
    var list = item.options_with_values || [];
    var out = [];
    list.forEach(function (o) {
      if (o.name === 'Title' && o.value === 'Default Title') return;
      var value = String(o.value).replace(/US Plug\s*110V/i, 'US Plug • 110V');
      out.push('<span class="dm-line__opt"><span class="dm-line__opt-name">' + DM.escape(o.name) + ':</span> ' + DM.escape(value) + '</span>');
    });
    return out.join('');
  }

  function linesHtml(cart) {
    if (!cart.items.length) {
      return (
        '<div class="dm-cart-empty"><p class="dm-cart-empty__text">' +
        DM.escape(str.cartEmpty) +
        '</p><a class="dm-btn dm-btn--primary" href="' +
        DM.escape(routes.allProducts) +
        '">' +
        DM.escape(str.cartEmptyCta) +
        '</a></div>'
      );
    }
    var html = '<ul class="dm-lines">';
    cart.items.forEach(function (item) {
      html += lineHtml(item);
    });
    return html + '</ul>';
  }

  function lineHtml(item) {
    var key = DM.escape(item.key);
    var title = DM.escape(item.product_title);
    var imageUrl = item.featured_image && item.featured_image.url ? item.featured_image.url : item.image;
    var media = imageUrl
      ? '<img class="dm-img dm-line__img" src="' + DM.escape(DM.sized(imageUrl, 160)) + '" alt="" width="80" height="96" loading="lazy">'
      : '<span class="dm-placeholder dm-line__img" aria-hidden="true"></span>';
    var discounts = '';
    (item.discounts || []).forEach(function (d) {
      discounts +=
        '<p class="dm-line__discount">' + DM.escape(d.title) + ': -' + DM.escape(DM.money(d.amount)) + '</p>';
    });
    var busy = state.lineBusy[item.key] ? ' is-busy' : '';
    var dis = state.lineBusy[item.key] ? ' disabled' : '';
    return (
      '<li class="dm-line' + busy + '" data-key="' + key + '">' +
      '<a class="dm-line__media" href="' + DM.escape(item.url) + '" tabindex="-1" aria-hidden="true">' + media + '</a>' +
      '<div class="dm-line__body">' +
      '<div class="dm-line__top">' +
      '<a class="dm-line__title" href="' + DM.escape(item.url) + '">' + title + '</a>' +
      '<button type="button" class="dm-line__remove" data-dm-remove data-dm-focus="rm-' + key + '" aria-label="' + DM.escape(DM.fmt(str.removeItem, { title: item.product_title })) + '"' + dis + '>' + DM.escape(str.remove) + '</button>' +
      '</div>' +
      '<p class="dm-line__opts">' + optionsText(item) + '</p>' +
      '<p class="dm-line__unit">' + DM.escape(DM.money(item.final_price)) + '</p>' +
      discounts +
      '<div class="dm-line__row">' +
      '<div class="dm-qty dm-qty--sm" role="group" aria-label="' + DM.escape(str.quantity + ': ' + item.product_title) + '">' +
      '<button type="button" class="dm-qty__btn" data-dm-dec data-dm-focus="dec-' + key + '" aria-label="' + DM.escape(DM.fmt(str.decrease, { title: item.product_title })) + '"' + dis + '><span aria-hidden="true">&minus;</span></button>' +
      '<input class="dm-qty__input" type="number" inputmode="numeric" min="0" max="999" value="' + item.quantity + '" data-dm-qty-input data-dm-focus="qty-' + key + '" aria-label="' + DM.escape(str.quantity + ': ' + item.product_title) + '"' + dis + '>' +
      '<button type="button" class="dm-qty__btn" data-dm-inc data-dm-focus="inc-' + key + '" aria-label="' + DM.escape(DM.fmt(str.increase, { title: item.product_title })) + '"' + dis + '><span aria-hidden="true">+</span></button>' +
      '</div>' +
      '<p class="dm-line__total">' + DM.escape(DM.money(item.final_line_price)) + '</p>' +
      '</div>' +
      '</div>' +
      '</li>'
    );
  }

  function markBusy(key, busy) {
    DM.$$('.dm-line').forEach(function (li) {
      if (li.getAttribute('data-key') === key) {
        li.classList.toggle('is-busy', busy);
        DM.$$('button, input', li).forEach(function (control) {
          control.disabled = busy;
        });
      }
    });
  }

  function footHtml(host, cart) {
    if (!cart.items.length) {
      host.hidden = true;
      host.innerHTML = '';
      return;
    }
    var sum = S.summarize(cart, shipOpts);
    var inDrawer = !!host.closest('[data-dm-modal]');
    var discountRow = '';
    if (cart.total_discount > 0) {
      /* v8: the spin-to-win credit is the discount code SPIN<amount>-XXXX (assets/dm-spin-wheel.js): show it as store credit on its own row. */
      var spinApp = (cart.cart_level_discount_applications || []).filter(function (a) {
        return /^SPIN\d+-/i.test(a.title || '');
      })[0];
      var spinAmt = spinApp ? Math.min(spinApp.total_allocated_amount || 0, cart.total_discount) : 0;
      if (spinAmt > 0) {
        discountRow +=
          '<div class="dm-sum__row dm-sum__row--saved dm-sum__row--credit"><dt>Store credit (spin win)</dt><dd>-' + DM.escape(DM.money(spinAmt)) + '</dd></div>';
      }
      if (cart.total_discount - spinAmt > 0) {
        discountRow +=
          '<div class="dm-sum__row dm-sum__row--saved"><dt>' + DM.escape(str.discount) + '</dt><dd>-' + DM.escape(DM.money(cart.total_discount - spinAmt)) + '</dd></div>';
      }
    }
    host.hidden = false;
    host.innerHTML =
      '<dl class="dm-sum">' +
      '<div class="dm-sum__row"><dt>' + DM.escape(str.subtotal) + '</dt><dd>' + DM.escape(DM.money(sum.subtotal + (cart.total_discount > 0 ? cart.total_discount : 0))) + '</dd></div>' +
      discountRow +
      '<div class="dm-sum__row"><dt>' + DM.escape(str.shippingEstimate) + '</dt><dd>' + (sum.shipping === 0 ? DM.escape(str.shippingFree) : DM.escape(DM.money(sum.shipping))) + '</dd></div>' +
      '<div class="dm-sum__row dm-sum__row--total"><dt>' + DM.escape(str.totalBeforeTax) + '</dt><dd>' + DM.escape(DM.money(sum.totalBeforeTax)) + '</dd></div>' +
      '</dl>' +
      '<form action="' + DM.escape(routes.cart) + '" method="post" class="dm-sum__form">' +
      '<button type="submit" name="checkout" class="dm-btn dm-btn--primary dm-btn--lg dm-btn--block">' + DM.escape(str.checkout || 'CHECKOUT') + '</button>' +
      '</form>' +
      (inDrawer ? '<button type="button" class="dm-link-btn dm-sum__continue" data-dm-close>' + DM.escape(str.continueShopping || 'Continue shopping') + '</button>' : '');
  }

  /* ---------- line interactions ---------- */
  function lineFromEvent(event) {
    var li = event.target instanceof Element ? event.target.closest('.dm-line[data-key]') : null;
    if (!li || !li.closest('[data-dm-cart-lines]')) return null;
    var key = li.getAttribute('data-key');
    var item = null;
    (state.cart ? state.cart.items : []).forEach(function (it) {
      if (it.key === key) item = it;
    });
    return item ? { li: li, item: item } : null;
  }

  doc.addEventListener('click', function (event) {
    var target = event.target;
    if (!(target instanceof Element)) return;
    var hit;
    if (target.closest('[data-dm-dec]')) {
      hit = lineFromEvent(event);
      if (hit) change(hit.item.key, Math.max(0, hit.item.quantity - 1), hit.item.product_title);
    } else if (target.closest('[data-dm-inc]')) {
      hit = lineFromEvent(event);
      if (hit) change(hit.item.key, hit.item.quantity + 1, hit.item.product_title);
    } else if (target.closest('[data-dm-remove]')) {
      hit = lineFromEvent(event);
      if (hit) change(hit.item.key, 0, hit.item.product_title);
    }
  });

  doc.addEventListener('change', function (event) {
    var input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.hasAttribute('data-dm-qty-input')) return;
    var hit = lineFromEvent(event);
    if (!hit) return;
    var value = parseInt(input.value, 10);
    if (isNaN(value) || value < 0) {
      input.value = hit.item.quantity;
      return;
    }
    if (value !== hit.item.quantity) change(hit.item.key, value, hit.item.product_title);
  });

  /* ---------- accessory upsell ---------- */
  function hasApparel(cart) {
    return cart.items.some(function (item) {
      return triggerHandles.indexOf(item.handle) > -1;
    });
  }
  function inCart(cart, productId) {
    return cart.items.some(function (item) {
      return String(item.product_id) === String(productId);
    });
  }

  function selectionFor(product) {
    var sel = state.upsellSel[product.handle] || [];
    var out = [];
    product.options.forEach(function (opt, i) {
      if (opt.values.length === 1) out[i] = opt.values[0].value;
      else out[i] = sel[i] || null;
    });
    return out;
  }
  function matching(product, sel) {
    return product.variants.filter(function (v) {
      return sel.every(function (val, i) {
        return val === null || val === undefined || v.options[i] === val;
      });
    });
  }
  function missingOption(product, sel) {
    for (var i = 0; i < product.options.length; i++) {
      if (!sel[i]) return product.options[i];
    }
    return null;
  }
  function isColor(opt) {
    return /^colou?r$/i.test(opt.name);
  }

  function upsellHtml(cart) {
    if (!cfg.upsell.enabled || !upsellData.length || !cart.items.length || !hasApparel(cart)) return '';
    var html =
      '<section class="dm-upsell" aria-labelledby="dm-upsell-title"><h3 class="dm-upsell__title" id="dm-upsell-title">' +
      DM.escape(cfg.upsell.heading || '') +
      '</h3><ul class="dm-upsell__list">';
    upsellData.forEach(function (product) {
      html += upsellCard(cart, product);
    });
    html += '</ul>';
    if (cfg.upsell.note) html += '<p class="dm-upsell__note">' + DM.escape(cfg.upsell.note) + '</p>';
    return html + '</section>';
  }

  function upsellCard(cart, product) {
    var sel = selectionFor(product);
    var cands = matching(product, sel);
    var complete = !missingOption(product, sel);
    var variant = complete && cands.length ? cands[0] : null;
    var added = inCart(cart, product.id);
    var busy = !!state.upsellBusy[product.handle];
    var sum = S.summarize(cart, shipOpts);

    var image = (variant && variant.image) || product.image;
    var price = variant ? variant.price : Math.min.apply(null, product.variants.map(function (v) { return v.price; }));
    var hintPrice = price;
    var unlock = !added && S.wouldUnlock(sum.subtotal, hintPrice, shipOpts.threshold);

    var optionsHtml = '';
    product.options.forEach(function (opt, i) {
      if (opt.values.length < 2) return;
      var cap = isColor(opt);
      optionsHtml += '<fieldset class="dm-upsell-card__opt"><legend class="dm-sr">' + DM.escape(opt.name) + '</legend><div class="dm-upsell-card__values">';
      opt.values.forEach(function (val, vi) {
        var trial = sel.slice();
        trial[i] = val.value;
        var cand = product.variants.filter(function (v) {
          return trial.every(function (x, j) {
            return !x || v.options[j] === x;
          });
        });
        var ok = cand.some(function (v) {
          return v.available;
        });
        var checked = sel[i] === val.value;
        var id = 'dm-up-' + product.handle + '-' + i + '-' + vi;
        optionsHtml +=
          '<input class="dm-opt__input" type="radio" id="' + id + '" name="dm-up-' + DM.escape(product.handle) + '-' + i + '" value="' + DM.escape(val.value) + '" data-dm-upsell-opt data-handle="' + DM.escape(product.handle) + '" data-index="' + i + '" data-dm-focus="' + id + '"' + (checked ? ' checked' : '') + (ok || checked ? '' : ' disabled') + (added ? ' disabled' : '') + '>' +
          '<label class="dm-opt__label dm-swatch dm-swatch--sm' + (ok ? '' : ' is-unavailable') + '" for="' + id + '">' +
          (cap ? '<span class="dm-swatch__dot" style="--dm-swatch:' + DM.escape(val.hex || '#C9CED3') + '"></span><span class="dm-sr">' + DM.escape(val.value) + (ok ? '' : ' ' + DM.escape(str.soldOutSr)) + '</span>' : '<span class="dm-size__text">' + DM.escape(val.value) + '</span>') +
          '</label>';
      });
      optionsHtml += '</div></fieldset>';
    });

    var label, disabled, cls;
    var missing = missingOption(product, sel);
    if (added) {
      label = str.upsellAdded;
      disabled = true;
      cls = ' is-added';
    } else if (busy) {
      label = str.upsellAdding;
      disabled = true;
      cls = '';
    } else if (missing) {
      label = DM.fmt(str.upsellChoose, { option: missing.name.toLowerCase() });
      disabled = false;
      cls = ' is-choose';
    } else if (!variant || !variant.available) {
      label = str.upsellSoldOut;
      disabled = true;
      cls = '';
    } else {
      label = str.upsellAdd;
      disabled = false;
      cls = '';
    }

    return (
      '<li class="dm-upsell-card' + (added ? ' is-added' : '') + '" data-handle="' + DM.escape(product.handle) + '">' +
      '<a class="dm-upsell-card__media" href="' + DM.escape(product.url) + '" tabindex="-1" aria-hidden="true">' +
      (image ? '<img class="dm-img dm-upsell-card__img" src="' + DM.escape(image) + '" alt="" width="72" height="72" loading="lazy">' : '<span class="dm-placeholder dm-upsell-card__img"></span>') +
      '</a>' +
      '<div class="dm-upsell-card__body">' +
      '<p class="dm-upsell-card__title"><a href="' + DM.escape(product.url) + '">' + DM.escape(product.title) + '</a></p>' +
      '<p class="dm-upsell-card__desc">' + DM.escape(product.description) + '</p>' +
      optionsHtml +
      '<p class="dm-upsell-card__price">' + DM.escape(DM.money(price)) + '</p>' +
      (unlock ? '<p class="dm-upsell-card__hint">' + DM.escape(str.unlockHint) + '</p>' : '') +
      '</div>' +
      '<button type="button" class="dm-btn dm-btn--cyan dm-btn--sm dm-upsell-card__btn' + cls + '" data-dm-upsell-add data-handle="' + DM.escape(product.handle) + '" data-dm-focus="upsell-' + DM.escape(product.handle) + '"' + (disabled ? ' disabled' : '') + '>' + DM.escape(label) + '</button>' +
      '</li>'
    );
  }

  doc.addEventListener('change', function (event) {
    var input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.hasAttribute('data-dm-upsell-opt')) return;
    var handle = input.getAttribute('data-handle');
    var index = parseInt(input.getAttribute('data-index'), 10);
    var sel = state.upsellSel[handle] || [];
    sel[index] = input.value;
    state.upsellSel[handle] = sel;
    render();
  });

  doc.addEventListener('click', function (event) {
    var btn = event.target instanceof Element ? event.target.closest('[data-dm-upsell-add]') : null;
    if (!btn || btn.disabled) return;
    var handle = btn.getAttribute('data-handle');
    var product = upsellData.filter(function (p) {
      return p.handle === handle;
    })[0];
    if (!product || state.upsellBusy[handle]) return;
    var sel = selectionFor(product);
    var missing = missingOption(product, sel);
    if (missing) {
      // Ask for the missing choice instead of guessing.
      var card = btn.closest('.dm-upsell-card');
      var first = card && card.querySelector('.dm-opt__input:not([disabled])');
      if (first) first.focus();
      DM.announce(DM.fmt(str.chooseOptionError, { option: missing.name.toLowerCase() }));
      return;
    }
    var variant = matching(product, sel)[0];
    if (!variant || !variant.available) return;
    state.upsellBusy[handle] = true;
    render();
    add([{ id: variant.id, quantity: 1 }], { open: false })
      .then(function () {
        delete state.upsellBusy[handle];
        render();
      })
      .catch(function (err) {
        delete state.upsellBusy[handle];
        render();
        if (err && err.message !== 'busy') showError(friendly(err));
      });
  });

  /* ---------- quick add buttons on product cards ---------- */
  doc.addEventListener('click', function (event) {
    var btn = event.target instanceof Element ? event.target.closest('[data-dm-quick-add]') : null;
    if (!btn || btn.disabled) return;
    var id = parseInt(btn.getAttribute('data-variant-id'), 10);
    if (!id) return;
    var label = btn.textContent;
    var note = btn.parentNode.querySelector('.dm-card__error');
    if (note) note.remove();
    btn.disabled = true;
    btn.setAttribute('aria-busy', 'true');
    btn.textContent = str.adding;
    add([{ id: id, quantity: 1 }])
      .then(function () {
        btn.disabled = false;
        btn.removeAttribute('aria-busy');
        btn.textContent = label;
      })
      .catch(function (err) {
        btn.disabled = false;
        btn.removeAttribute('aria-busy');
        btn.textContent = label;
        if (err && err.message === 'busy') return;
        var p = doc.createElement('p');
        p.className = 'dm-card__error';
        p.setAttribute('role', 'alert');
        p.textContent = friendly(err);
        btn.parentNode.appendChild(p);
      });
  });

  /* ---------- public API ---------- */
  window.DMCart = {
    add: add,
    change: change,
    open: open,
    close: close,
    refresh: fetchCart,
    get: function () {
      return state.cart;
    }
  };

  /* ---------- boot ---------- */
  function boot() {
    fetchCart().catch(function () {
      /* Server-rendered count and progress stay as they are. */
    });
  }
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) boot();
  });
  boot();
})();
